import {FC, useMemo} from "react";
import cx from 'classnames'
import {Button, Checkbox, Flex, Form, Select, SelectProps} from "antd";
import {MinusCircleOutlined, PlusOutlined} from "@ant-design/icons";
import styles from './AirportWithTimeSelector.module.css';
import {TimeInput} from "../../../../components/TimeInput/TimeInput.tsx";
import dayjs, {Dayjs} from "dayjs";
import {Airport, AirportWithTimePost} from "../../../../types/domain.ts";

type Props = {
    airports: Airport[] | undefined;
    name: string;
    label: string;
    reversed?: boolean;
    timeEnforcer?: boolean;
    timeEnforced?: boolean;
    onTimeEnforcementChange?: (timeEnforced: boolean) => void;
    restrictRepeats?: boolean;
}

export const AirportWithTimeSelector: FC<Props> = ({
    airports,
    name: listName,
    label,
    reversed,
    timeEnforcer,
    timeEnforced,
    onTimeEnforcementChange,
    restrictRepeats
}) => {
    const form = Form.useFormInstance();
    const values = Form.useWatch(listName, form) as AirportWithTimePost[];

    const airportsOptions = useMemo(() => {
        const reduced = airports?.reduce<Exclude<SelectProps['options'], undefined>>((acc, { id, name, fir }) => {
            if(values?.filter(v => v).find(({ airport_id }) => airport_id === id) && restrictRepeats) return acc;

            const regionName = fir?.region.name || 'Вне VATRUS';
            const existingRegion = acc.find(({ title }) => title === regionName);

            if (!existingRegion) {
                return [
                    ...acc,
                    {
                        title: regionName,
                        label: regionName,
                        options: [{ label: `${id} ${name} ${fir ? `(РПИ ${fir.name})` : '(Вне VATRUS)'}`, value: id }],
                    },
                ];
            }

            existingRegion.options.push({ label: `${id} ${name} ${fir ? `(РПИ ${fir.name})` : '(Вне VATRUS)'}`, value: id });
            return [...acc];
        }, []);

        if(!reduced) return;

        const withoutVatrus = reduced.filter(g => g.title !== 'Вне VATRUS');
        const vatrusGroup = reduced.find(g => g.title === 'Вне VATRUS');

        return vatrusGroup ? [...withoutVatrus, vatrusGroup] : withoutVatrus;
    }, [airports, values]);

    return (
        <Form.Item
            label={(
                <Flex style={{ width: '100%' }} align="center" justify="space-between">
                    {label}
                    {timeEnforcer && (
                       <Checkbox
                           checked={timeEnforced}
                           onChange={({ target }) => onTimeEnforcementChange?.(target.checked)}
                       >Время совпадает во всех портах</Checkbox>
                    )}
                </Flex>
            )}
            className={cx(styles.grow, styles.fullWidthLabel)}
            style={{ minWidth: 0 }}
        >
            <Form.List name={listName}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                            <Flex key={key} align="center" gap={8}>
                                <Flex flex="1 0" align="center" gap={8} className={cx(styles.minWidth100, { [styles.reversed]: reversed })}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'airport_id']}
                                        className={cx(styles.minWidth100, styles.grow)}
                                        rules={[{ type: "string", required: true }]}
                                    >
                                        <Select
                                            showSearch
                                            options={airportsOptions}
                                            optionFilterProp="label"
                                        />
                                    </Form.Item>
                                    <Flex align="center" gap={8}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'from']}
                                            rules={[{ required: true }]}
                                            normalize={(value?: Dayjs) => value ? value.utc(true).format('HH:mm') : undefined}
                                        >
                                            <TimeInput
                                                placeholder="От"
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'to']}
                                            dependencies={[[listName, name, 'from']]}
                                            rules={[
                                                { required: true },
                                                ({ getFieldValue }) => ({
                                                    validator(_, to: string) {
                                                        const from: string = getFieldValue([listName, name, 'from']);
                                                        if(dayjs(from, 'HH:mm').utc(true).isAfter(dayjs(to, 'HH:mm').utc(true))) return Promise.reject(new Error('Ошибка'));
                                                        return Promise.resolve();
                                                    }
                                                })
                                            ]}
                                            normalize={(value?: Dayjs) => value ? value.utc(true).format('HH:mm') : undefined}
                                        >
                                            <TimeInput
                                                placeholder="До"
                                            />
                                        </Form.Item>
                                    </Flex>
                                </Flex>
                                {fields.length > 1 && (
                                    <MinusCircleOutlined
                                        className={styles.deleteButton}
                                        onClick={() => remove(name)}
                                    />
                                )}
                            </Flex>
                        ))}
                        <Form.Item>
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                Добавить
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List>
        </Form.Item>
    )
}